import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { servicesAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { Pencil, Trash2, Plus } from 'lucide-react';

const ICON_OPTIONS = ['MapPin', 'Briefcase', 'Plane', 'Clock', 'Navigation', 'Building'];
const EMPTY = { title: '', description: '', icon: 'MapPin', features: '' };

export const ServicesManager = () => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await servicesAPI.getAll();
      setServices(res.data);
    } catch (e) {
      console.error('Failed to load services', e);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ title: s.title, description: s.description, icon: s.icon, features: (s.features || []).join(', ') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      icon: form.icon,
      features: form.features.split(',').map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await servicesAPI.update(editingId, payload);
        toast({ title: 'Success', description: 'Service updated' });
      } else {
        await servicesAPI.create(payload);
        toast({ title: 'Success', description: 'Service added' });
      }
      resetForm();
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save service', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await servicesAPI.remove(id);
      toast({ title: 'Deleted', description: 'Service removed' });
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Service' : 'Add New Service'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="service-title-input" />
              </div>
              <div>
                <Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="service-description-input" />
            </div>
            <div>
              <Label>Features (comma separated)</Label>
              <Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="One-way & Round-trip, Professional drivers" data-testid="service-features-input" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} data-testid="service-save-button">
                {editingId ? 'Update Service' : <><Plus className="h-4 w-4 mr-1" />Add Service</>}
              </Button>
              {editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {services.map((s) => (
          <div key={s.id} className="border rounded-lg p-4 flex items-start justify-between bg-white" data-testid={`service-row-${s.id}`}>
            <div>
              <h4 className="font-semibold">{s.title} <span className="text-xs text-gray-400">({s.icon})</span></h4>
              <p className="text-sm text-gray-600">{s.description}</p>
              <p className="text-xs text-gray-500 mt-1">{(s.features || []).join(' • ')}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-4">
              <Button size="sm" variant="outline" onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(s.id)} data-testid={`service-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesManager;
